import { useEffect, useRef, useState } from "react";
import { ReactComponent as UploadCardIcon } from "../../assets/icons/UploadCardIcon.svg";
import UploadCardCrooperModal from "./UploadCardCrooperModal";
import { Crop, Gallery, InfoCircle, Trash } from "iconsax-reactjs";
import Button from "./Button";

type UploadCardInitialValue =
  | {
      type: "image";
      previewUrl: string;
    }
  | {
      type: "video";
      previewUrl: string;
    };

type UploadCardProps = {
  title: string;
  description: string;
  helperText: string;
  mode: "image" | "video" | "mixed";
  onChange: (file: File | null) => void;
  cropAspect?: number;
  initialValue?: UploadCardInitialValue;
};

export default function UploadCard({ title, description, helperText, mode, onChange, cropAspect, initialValue }: UploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hydratedRef = useRef(false);

  const [fileType, setFileType] = useState<"image" | "video" | null>(null);
  const [previewImage, setPreviewImage] = useState<string>();
  const [previewVideo, setPreviewVideo] = useState<string>();
  const [tempImage, setTempImage] = useState<string>();
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!initialValue || hydratedRef.current) return;

    hydratedRef.current = true;

    if (initialValue.type === "image") {
      setFileType("image");
      setPreviewImage(initialValue.previewUrl);
      setPreviewVideo(undefined);
    }

    if (initialValue.type === "video") {
      setFileType("video");
      setPreviewVideo(initialValue.previewUrl);
      setPreviewImage(undefined);
    }
  }, [initialValue]);

  useEffect(() => {
    return () => {
      if (tempImage) URL.revokeObjectURL(tempImage);
    };
  }, [tempImage]);

  const openFilePicker = (type: "image" | "video") => {
    const input = inputRef.current;
    if (!input) return;

    input.accept = type === "image" ? "image/*" : "video/mp4,video/webm";
    setFileType(type);
    input.click();
  };

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fileType) return;

    if (fileType === "image") {
      const url = URL.createObjectURL(file);
      setTempImage(url);
      setCropperOpen(true);
    } else {
      const url = URL.createObjectURL(file);
      setPreviewVideo(url);
      setPreviewImage(undefined);
      onChange(file);
    }

    e.target.value = "";
  };

  const handleCropperClose = () => {
    if (tempImage) URL.revokeObjectURL(tempImage);
    setTempImage(undefined);
    setCropperOpen(false);
  };

  const handleCropperApply = (file: File, previewUrl: string) => {
    if (tempImage) URL.revokeObjectURL(tempImage);
    setPreviewImage(previewUrl);
    setPreviewVideo(undefined);
    onChange(file);
    setTempImage(undefined);
    setCropperOpen(false);
  };

  const handleRemove = () => {
    setPreviewImage(undefined);
    setPreviewVideo(undefined);
    setImageError(false);
    setFileType(null);
    onChange(null);
  };

  const handleEditImage = async () => {
    if (!previewImage) return;
    const res = await fetch(previewImage);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    setTempImage(url);
    setCropperOpen(true);
  };

  const hasPreview = !!previewImage || !!previewVideo;

  return (
    <div className="bg-white border rounded-2xl card-shadow py-6 px-4 flex-1">
      {fileType === "image" && (
        <UploadCardCrooperModal
          open={cropperOpen}
          imageSrc={tempImage}
          onClose={handleCropperClose}
          onApply={handleCropperApply}
          aspect={cropAspect}
        />
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleSelectFile}
      />

      <h3 className="relative pr-3 leading-7 font-bold text-xl mb-3 text-primaryVar-500 before:absolute before:right-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-1 before:rounded before:bg-primaryVar-500">
        {title}
      </h3>

      <p className="font-semibold text-xs text-gray-500">{description}</p>

      <div className="my-6 h-px -mx-4 w-[calc(100%+32px)] bg-[repeating-linear-gradient(to_right,#D1D5DB_0_7px,transparent_7px_14px)]" />

      <div className="relative overflow-hidden h-64 bg-gray-50 border border-gray-300 rounded-2xl flex flex-col justify-evenly items-center px-4">
        {hasPreview ? (
          <>
            {previewImage && !imageError && (
              <img
                src={previewImage}
                alt=""
                onError={() => setImageError(true)}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}

            {previewVideo && (
              <video
                src={previewVideo}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
              <Button
                variant="neutral"
                style="solid"
                size="md"
                text={fileType === "image" ? "تغییر تصویر" : "تغییر ویدیو"}
                loading={false}
                disabled={false}
                onClick={() => openFilePicker(fileType!)}
                icon={Gallery}
                customClassName="shadow-[0_0_10px_rgba(0,0,0,0.25)]"
              />

              <Button
                variant="neutral"
                style="solid"
                size="md"
                text=""
                loading={false}
                disabled={false}
                onClick={handleRemove}
                icon={Trash}
                customClassName="shadow-[0_0_10px_rgba(0,0,0,0.25)]"
              />

              {fileType === "image" && (
                <Button
                  variant="neutral"
                  style="solid"
                  size="md"
                  text=""
                  loading={false}
                  disabled={false}
                  onClick={handleEditImage}
                  icon={Crop}
                  customClassName="shadow-[0_0_10px_rgba(0,0,0,0.25)]"
                />
              )}
            </div>
          </>
        ) : (
          <>
            <UploadCardIcon />

            <p className="font-semibold text-xs text-gray-400">
              فایل چندرسانه‌ای را انتخاب کنید
            </p>

            {mode === "mixed" ? (
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  style="outline"
                  size="md"
                  text="انتخاب تصویر"
                  loading={false}
                  disabled={false}
                  onClick={() => openFilePicker("image")}
                />

                <Button
                  variant="primary"
                  style="outline"
                  size="md"
                  text="انتخاب ویدیو"
                  loading={false}
                  disabled={false}
                  onClick={() => openFilePicker("video")}
                />
              </div>
            ) : (
              <Button
                variant="primary"
                style="outline"
                size="md"
                text={mode === "video" ? "انتخاب ویدیو" : "انتخاب تصویر"}
                loading={false}
                disabled={false}
                onClick={() => openFilePicker("image")}
              />
            )}

            <p className="flex items-center gap-1 font-semibold text-xs text-gray-400 self-start">
              <InfoCircle variant="Outline" size="18"/>
              {helperText}
            </p>
          </>
        )}
      </div>
    </div>
  );
};